/**
 * Meta Pixel helpers.
 * The base pixel + initial PageView fire from public/index.html.
 * This module handles SPA route changes and conversion events.
 */

/**
 * Fire a standard Meta event, e.g. trackEvent('Lead')
 *
 * `eventId` deduplicates against the same event sent server-side by the
 * Conversions API. Meta merges on (event_name, event_id) — for payments we
 * pass the Stripe PaymentIntent id, which both sides already know. Omitting
 * it on a Purchase means that sale gets counted twice.
 */
export function trackEvent(name, params, eventId) {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  try {
    if (eventId) {
      window.fbq('track', name, params || undefined, { eventID: String(eventId) });
    } else {
      window.fbq('track', name, params || undefined);
    }
  } catch (e) {
    /* never let analytics break the page */
  }
}

/** Fire a custom (non-standard) Meta event */
export function trackCustom(name, params) {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  try {
    window.fbq('trackCustom', name, params || undefined);
  } catch (e) { /* noop */ }
}

/** PageView — call on every client-side route change */
export function trackPageView() {
  trackEvent('PageView');
}

/* ───────────── Meta click identifiers ─────────────
   fbc (click ID) and fbp (browser ID) are the strongest attribution signals
   available — fbc ties a purchase to one specific ad click rather than
   relying on a fuzzy match against a hashed email.

   The pixel writes both as first-party cookies. We read them at checkout and
   hand them to our server, which forwards them with the Conversions API
   Purchase so browser and server events describe the same person.

   These are Meta's own identifiers, not personal data, and per Meta's spec
   they must be sent unhashed. */

function readCookie(name) {
  if (typeof document === 'undefined') return '';
  const match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[2]) : '';
}

/**
 * Meta's click ID.
 *
 * Normally the pixel stores this as `_fbc`. But on the very first pageview
 * after an ad click the cookie may not be written yet, so we rebuild it from
 * the `fbclid` query parameter using Meta's documented format:
 *
 *   fb.{subdomainIndex}.{creationTime}.{fbclid}
 *
 * Missing this window matters: the first pageview after a click is exactly
 * when someone is most likely to buy.
 */
export function getFbc() {
  const cookie = readCookie('_fbc');
  if (cookie) return cookie;

  try {
    const fbclid = new URLSearchParams(window.location.search).get('fbclid');
    if (fbclid) return `fb.1.${Date.now()}.${fbclid}`;
  } catch (e) { /* no URL access */ }

  return '';
}

/** Meta's browser ID — set by the pixel, stable across visits */
export function getFbp() {
  return readCookie('_fbp');
}

/** Both identifiers, ready to send to our server at checkout */
export function getClickIds() {
  return { fbc: getFbc(), fbp: getFbp() };
}
